/**
 * Audit · Ticket Purges — /admin/audit/ticket-purges (Iter 121)
 *
 * Superadmin-only view that surfaces every destructive ticket action:
 *   • crm.ticket.bulk_delete — successful purge
 *   • crm.ticket.bulk_delete.wrong_password — failed attempt
 *   • crm.ticket.archive_restore — restore from archive
 *
 * Read-only. Actor's name/email are resolved server-side so the row
 * reads clearly even if the user was renamed since the event.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Warning, ArrowClockwise, Trash, MagnifyingGlass,
} from "@phosphor-icons/react";

const ACTION_META = {
  "crm.ticket.bulk_delete": {
    label: "Bulk delete", Icon: Trash,
    color: "bg-[#F4C7B9] text-[#7A2E18] border-[#EFC7B6]",
  },
  "crm.ticket.bulk_delete.wrong_password": {
    label: "Wrong password", Icon: Warning,
    color: "bg-[#F9E4B7] text-[#7A5A11] border-[#F0DDB2]",
  },
  "crm.ticket.archive_restore": {
    label: "Restore", Icon: ArrowClockwise,
    color: "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]",
  },
};

export default function AuditTicketPurges() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit/ticket-purges?limit=500");
      setRows(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter(r =>
      (r.actor_name || "").toLowerCase().includes(qq) ||
      (r.actor_email || "").toLowerCase().includes(qq) ||
      (r.target || "").toLowerCase().includes(qq) ||
      (r.action || "").toLowerCase().includes(qq)
    );
  }, [rows, q]);

  // Small dashboard-style counters for the top of the page
  const counts = useMemo(() => {
    const c = { bulk: 0, wrong: 0, restore: 0 };
    for (const r of rows) {
      if (r.action === "crm.ticket.bulk_delete") c.bulk++;
      else if (r.action === "crm.ticket.bulk_delete.wrong_password") c.wrong++;
      else if (r.action === "crm.ticket.archive_restore") c.restore++;
    }
    return c;
  }, [rows]);

  return (
    <AppShell title="Audit · Ticket Purges"
      subtitle="Superadmin · destructive-action trail">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4"
           data-testid="apurge-counters">
        <Counter icon={Trash} label="Bulk deletes" value={counts.bulk}
                  bg="bg-[#FDECEC]" fg="text-[#7A2E18]"/>
        <Counter icon={Warning} label="Wrong-password attempts" value={counts.wrong}
                  bg="bg-[#FFFDF5]" fg="text-[#7A5A11]"/>
        <Counter icon={ArrowClockwise} label="Restores" value={counts.restore}
                  bg="bg-[#F0F7EE]" fg="text-[#1A3317]"/>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md p-3 mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-[#8A8780]"/>
          <input value={q} onChange={e => setQ(e.target.value)}
                  placeholder="Search actor, target or action…"
                  data-testid="apurge-search"
                  className="w-full h-9 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="apurge-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Target ids</th>
              <th className="px-4 py-3">Meta</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-[#8A8780]">
                Loading audit trail…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-14 text-center text-[#8A8780]">
                <ShieldCheck size={26} className="inline-block mb-3 opacity-40"/><br/>
                No destructive events recorded yet.
              </td></tr>
            )}
            {filtered.map((r, i) => {
              const meta = ACTION_META[r.action] || {
                label: r.action, Icon: ShieldCheck, color: "bg-[#F0EFEA] text-[#5C5A55] border-[#E5E2D9]",
              };
              const Icon = meta.Icon;
              const targetIds = String(r.target || "").split(",").filter(Boolean);
              return (
                <tr key={i} data-testid={`apurge-row-${i}`}
                    className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-4 py-3 whitespace-nowrap text-[12px] text-[#5C5A55]">
                    {r.at ? new Date(r.at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded-full border ${meta.color}`}>
                      <Icon size={10} weight="bold"/>{meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[13px]">{r.actor_name || "—"}</div>
                    <div className="text-[10px] text-[#8A8780]">{r.actor_email || r.user_id || "?"}</div>
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono text-[#5C5A55] max-w-sm">
                    <div className="line-clamp-2 break-all">{r.target || "—"}</div>
                    {targetIds.length > 1 && (
                      <div className="text-[9px] text-[#8A8780] mt-0.5">
                        {targetIds.length} id(s)
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#5C5A55]">
                    {r.meta ? (
                      <div className="space-y-0.5">
                        {r.meta.deleted != null && (
                          <div><strong className="text-[#1A3317]">{r.meta.deleted}</strong> deleted</div>
                        )}
                        {r.meta.skipped != null && r.meta.skipped > 0 && (
                          <div><strong className="text-[#7A5A11]">{r.meta.skipped}</strong> skipped</div>
                        )}
                      </div>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

function Counter({ icon: Icon, label, value, bg, fg }) {
  return (
    <div className={`${bg} border border-[#E5E2D9] rounded-md p-3`}>
      <div className="flex items-center gap-2">
        <Icon size={18} weight="fill" className={fg}/>
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#5C5A55]">{label}</div>
      </div>
      <div className={`text-2xl font-heading mt-1 ${fg}`}>{value}</div>
    </div>
  );
}
